// The popup half of `uib-tooltip` / `uib-tooltip-template` with `tooltip-append-to-body`.
//
// ⚠️ IT IS PORTALLED TO `<body>`, AND THAT IS NOT A LIBERTY. Upstream's popups are appended to
// the body (measured: `parentElement` is `BODY` for both the disabled-Send tooltip and the
// Message History one), and their stylesheets are written for it — the Message History wrap
// is `position: fixed !important` at `top: 62px; left: -4px`, i.e. against the frame, and the
// Send tooltip is placed in page coordinates. Rendered inside the trigger instead, both would
// be clipped by the composer's and the list's `overflow` and inherit their colour and
// line-height.
//
// The other tooltips in this port (`SlimHeaderDropdown`, `CandidateFiltersBar`) render the same
// chrome in place, anchored by an inline `top: 100%` — they predate this, and each says why.
// New ones should use this.
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { arrowStyle, hostOffset, positionElements, uibTooltipClass, type UibPlacement } from './uibTooltip.ts'

export interface UibTooltipPopupProps {
  /** The element carrying the `uib-tooltip*` attributes — what the popup is placed against. */
  host: RefObject<HTMLElement | null>
  /** `tooltip-placement`. */
  placement: UibPlacement
  /** `tooltip-class` — upstream's `popup-class`, which the stylesheets hang everything off. */
  popupClass?: string
  /** What `.tooltip-inner` holds: the `uib-tooltip` text, or the `uib-tooltip-template`. */
  children: ReactNode
}

export function UibTooltipPopup({ host, placement, popupClass, children }: UibTooltipPopupProps): ReactElement | null {
  const popup = useRef<HTMLDivElement>(null)
  // uib adds `in` a digest after the popup is attached, which is what runs `.fade`'s opacity
  // transition. Rendering it on the first paint would skip the fade.
  const [isIn, setIn] = useState(false)
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIn(true)
    })
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [])

  // ⚠️ EVERY RENDER, NO DEPENDENCY LIST. uib repositions on every digest while open, because the
  // content can change its size (the Send copy grows a clause when the signature is edited).
  // Written to the DOM directly rather than through `style`, so React never re-applies a
  // stale value over the measured one.
  useLayoutEffect(() => {
    const element = popup.current
    const anchor = host.current
    if (element === null || anchor === null) return
    const position = positionElements(
      hostOffset(anchor.getBoundingClientRect(), window.scrollX, window.scrollY),
      { width: element.offsetWidth, height: element.offsetHeight },
      placement,
    )
    element.style.top = `${String(position.top)}px`
    element.style.left = `${String(position.left)}px`
    element.style.visibility = 'visible'
    const arrow = element.querySelector<HTMLElement>('.tooltip-arrow')
    const inner = element.querySelector<HTMLElement>('.tooltip-inner')
    if (arrow === null || inner === null) return
    const [primary, secondary] = placement.split('-')
    const corner = primary === 'top' || primary === 'bottom' ? `${primary}-${secondary ?? ''}` : `${secondary ?? ''}-${primary ?? ''}`
    const radius = getComputedStyle(inner).getPropertyValue(`border-${corner}-radius`)
    const css = arrowStyle(placement, radius)
    if (css === undefined) return
    for (const [property, value] of Object.entries(css)) arrow.style.setProperty(property, value)
  })

  if (typeof document === 'undefined') return null
  return createPortal(
    <div ref={popup} className={uibTooltipClass(placement, popupClass, isIn)} style={{ visibility: 'hidden', display: 'block', top: 0, left: 0 }}>
      <div className="tooltip-arrow" />
      <div className="tooltip-inner">{children}</div>
    </div>,
    document.body,
  )
}

export interface HoverTooltipState {
  open: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
}

/**
 * The default `mouseenter`/`mouseleave` trigger of a text `uib-tooltip`, with `tooltip-enable`.
 *
 * ⚠️ `enable` IS READ WHEN THE POINTER ARRIVES, NOT CONTINUOUSLY — uib evaluates
 * `tooltip-enable` in its show handler only. What it DOES watch is the content: an empty
 * `uib-tooltip` hides an open popup (`attrs.$observe` → `hide()`), and never opens one. So a
 * Send that becomes sendable under the pointer loses its tooltip because the copy went empty,
 * not because `enable` flipped.
 */
export function useHoverTooltip(content: string | undefined, enable: boolean): HoverTooltipState {
  const [open, setOpen] = useState(false)
  const hasContent = content !== undefined && content !== ''
  useEffect(() => {
    if (!hasContent) setOpen(false)
  }, [hasContent])
  return {
    open: open && hasContent,
    onMouseEnter: () => {
      if (enable && hasContent) setOpen(true)
    },
    onMouseLeave: () => {
      setOpen(false)
    },
  }
}
